import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import ApplicationRow from "./ApplicationRow";
import type { ListApplication } from "~/types/application.types";
import NoApplications from "./NoApplications";
import type { ComponentType } from "react";
import { ArrowUp, ArrowDown } from "lucide-react";
import { Spinner } from "~/components/ui/spinner";
import { TableCell } from "~/components/ui/table";

type Application = ListApplication;

type SortableColumn = "title" | "category" | "status" | "updatedAt" | "owner";

type ApplicationsTableViewProps = {
  applications: Application[];
  RowComponent?: ComponentType<{
    application: Application;
    isCollaboratedView?: boolean;
    showOwner?: boolean;
  }>;
  isCollaboratedView?: boolean;
  showOwner?: boolean;
  sort?: string;
  order?: string;
  onSort?: (column: SortableColumn) => void;
  loading?: boolean;
};

function SortableHeader({
  label,
  column,
  activeSort,
  activeOrder,
  onSort,
}: {
  label: string;
  column: SortableColumn;
  activeSort?: string;
  activeOrder?: string;
  onSort?: (column: SortableColumn) => void;
}) {
  const isActive = activeSort === column;
  const Icon = activeOrder === "asc" ? ArrowUp : ArrowDown;

  return (
    <TableHead
      className="font-semibold text-foreground text-xs uppercase tracking-wide py-4 px-6 cursor-pointer select-none hover:bg-gray-100 transition-colors"
      onClick={() => onSort?.(column)}
    >
      <span className="inline-flex items-center gap-1.5">
        {label}
        {isActive && (
          <Icon className="w-3.5 h-3.5 shrink-0 text-brand-green-primary" />
        )}
      </span>
    </TableHead>
  );
}

export default function ApplicationsTableView({
  applications,
  RowComponent = ApplicationRow,
  isCollaboratedView = false,
  showOwner = false,
  sort,
  order,
  onSort,
  loading = false,
}: ApplicationsTableViewProps) {
  return (
    <div className="w-full min-w-0">
      {!loading && applications.length === 0 ? (
        <NoApplications isCollaboratedView={isCollaboratedView} />
      ) : (
        <div className="w-full overflow-auto max-h-[calc(100vh-300px)]">
          <Table className="min-w-[840px] w-full">
            <TableHeader className="sticky top-0 z-10">
              <TableRow className="bg-gray-50">
                <SortableHeader
                  label="Title"
                  column="title"
                  activeSort={sort}
                  activeOrder={order}
                  onSort={onSort}
                />
                {showOwner && (
                  <SortableHeader
                    label="Owner"
                    column="owner"
                    activeSort={sort}
                    activeOrder={order}
                    onSort={onSort}
                  />
                )}
                <SortableHeader
                  label="Category"
                  column="category"
                  activeSort={sort}
                  activeOrder={order}
                  onSort={onSort}
                />
                {isCollaboratedView && (
                  <TableHead className="font-semibold text-foreground text-xs uppercase tracking-wide py-4 px-6">
                    Role
                  </TableHead>
                )}
                <SortableHeader
                  label="Last Updated"
                  column="updatedAt"
                  activeSort={sort}
                  activeOrder={order}
                  onSort={onSort}
                />
                <SortableHeader
                  label="Status"
                  column="status"
                  activeSort={sort}
                  activeOrder={order}
                  onSort={onSort}
                />
                <TableHead className="font-semibold text-foreground text-xs uppercase tracking-wide py-4 px-6">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <TableRow>
                  <TableCell colSpan={99} className="py-16 text-center">
                    <Spinner className="size-6 text-brand-green-primary mx-auto" />
                  </TableCell>
                </TableRow>
              ) : (
                applications.map((application) => (
                  <RowComponent
                    key={application.id}
                    application={application}
                    isCollaboratedView={isCollaboratedView}
                    showOwner={showOwner}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
